import { useState, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'

import { api } from '../../services/api'
import {
  TOAST_ERROR_DEFAULT_OPTIONS,
  TOAST_SUCCESS_DEFAULT_OPTIONS,
} from '../../constants/toast'
import { useTranslation } from '../../contexts/TranslationContext'
import { getErrorMessage } from '../../utils/getErrorMessage'
import { type ExpertDomain } from '../../types/domain/expert-domain'
import { type SkillDomain } from '../../types/domain/skill-domain'

type TranslationLabel = 'portugueseName' | 'englishName' | 'spanishName'

export const useExpertSkills = (expertId: number) => {
  const { formatMessage } = useIntl()

  const [isLoadingExpertSkills, setIsLoadingExpertSkills] = useState(true)
  const [isLoadingAreaSkills, setIsLoadingAreaSkills] = useState(true)
  const [isLoadingDomainSkills, setIsLoadingDomainSkills] = useState(true)

  const [isUpdatingSkills, setIsUpdatingSkills] = useState(false)
  const [expert, setExpert] = useState<ExpertDomain>()
  const [areaSkills, setAreaSkills] = useState<SkillDomain[]>()
  const [filteredAreaSkills, setFilteredAreaSkills] = useState<SkillDomain[]>()
  const [domainSkills, setDomainSkills] = useState<SkillDomain[]>()
  const [filteredDomainSkills, setFilteredDomainSkills] =
    useState<SkillDomain[]>()
  const [translationLabel, setTranslationLabel] =
    useState<TranslationLabel>('portugueseName')
  const [currentLocale] = useTranslation()

  useEffect(() => {
    currentLocale === 'pt' && setTranslationLabel('portugueseName')
    currentLocale === 'en' && setTranslationLabel('englishName')
    currentLocale === 'es' && setTranslationLabel('spanishName')
  }, [currentLocale])

  useEffect(() => {
    const fetchExpert = async (id: number) => {
      setIsLoadingExpertSkills(true)

      api
        .get(`/experts/${id}/expert-skills`)
        .then((result) => {
          setExpert(result.data)
        })
        .catch(() => {
          toast.error(
            formatMessage({
              id: 'AN_ERROR_OCCURRED_IN_AN_ATTEMP_TO_OBTAIN_THE_SKILLS_OF_THE_EXPERT',
            }),
            TOAST_ERROR_DEFAULT_OPTIONS
          )
        })
        .finally(() => setIsLoadingExpertSkills(false))
    }

    const fetchAreaSkills = async () => {
      try {
        setIsLoadingAreaSkills(true)
        const { data } = await api.get('/experts/area-skills')
        setAreaSkills(data)
      } catch (error) {
        toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
      } finally {
        setIsLoadingAreaSkills(false)
      }
    }

    const fetchDomainSkills = async () => {
      try {
        setIsLoadingDomainSkills(true)

        const { data } = await api.get('/experts/domain-skills')
        setDomainSkills(data)
      } catch (error) {
        toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
      } finally {
        setIsLoadingDomainSkills(false)
      }
    }

    fetchExpert(expertId)
    fetchAreaSkills()
    fetchDomainSkills()
  }, [expertId])

  const updateSkills = async (data: any) => {
    const { areaSkills, domainSkills } = data

    const areaSkillsIds = areaSkills
      .filter((areaSkill: any) => areaSkill.id !== 'false')
      .map((areaSkill: any) => areaSkill.id)

    const domainSkillsIds = domainSkills
      .filter((domainSkill: any) => domainSkill.id !== 'false')
      .map((domainSkill: any) => domainSkill.id)

    setIsUpdatingSkills(true)

    try {
      await Promise.all([
        api.put(`/experts/${expertId}/area-skills`, {
          areaSkillsIds,
        }),
        api.put(`/experts/${expertId}/domain-skills`, {
          domainSkillsIds,
        }),
      ])

      toast(
        formatMessage({ id: 'SKILLS_SUCCESSFULLY_SAVED' }),
        TOAST_SUCCESS_DEFAULT_OPTIONS
      )
    } catch (error) {
      toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
    } finally {
      setIsUpdatingSkills(false)
    }
  }

  const handleSearchAreaSkills = (therm: string) => {
    if (therm === '') {
      setFilteredAreaSkills(areaSkills)
      return
    }

    setFilteredAreaSkills(
      areaSkills?.filter((skill) =>
        skill[translationLabel].toLowerCase().includes(therm.toLowerCase())
      )
    )
  }

  const handleSearchDomainSkills = (therm: string) => {
    if (therm === '') {
      setFilteredDomainSkills(domainSkills)
      return
    }

    setFilteredDomainSkills(
      domainSkills?.filter((skill) =>
        skill[translationLabel].toLowerCase().includes(therm.toLowerCase())
      )
    )
  }

  return {
    expert,
    areaSkills,
    isLoadingExpertSkills,
    isLoadingAreaSkills,
    isLoadingDomainSkills,
    domainSkills,
    filteredAreaSkills,
    filteredDomainSkills,
    translationLabel,
    isUpdatingSkills,
    updateSkills,
    handleSearchAreaSkills,
    handleSearchDomainSkills,
  }
}
